import * as THREE from "three";
import { MathUtils } from "three";
import { State } from "../entities/OfferItem";
var degToRad = MathUtils.degToRad;
/** Creates 2 * 4 invisible objects on each side of the model. 4 for the frames to pick the extras, 4 for the wall. If we hover on them the {@link raycastHandler} will change the material's visibility.
 * The meshes have the names according to their sides, so we can detect which side has been clicked later.
 * When wallpicker is visible, we do not show the extra picker and vica versa.
 */
export class SidePicker {
    constructor(scene) {
        this.wallSideMaterialContainer = new WallSideMaterialContainer();
        this.scene = scene;
        if (scene.formData.state === State.WALLSELECTING)
            this.createWallSidePickers(scene);
        else if (scene.formData.state === State.EXTRASELECTING)
            this.createExtraSidePickers(scene);
    }
    createExtraSidePickers(scene) {
        this.createExtraSidePicker(scene.dimensions.left, scene, "LEFT");
        this.createExtraSidePicker(scene.dimensions.right, scene, "RIGHT");
        this.createExtraSidePicker(scene.dimensions.far, scene, "FAR");
        this.createExtraSidePicker(scene.dimensions.close, scene, "CLOSE");
    }
    createExtraSidePicker(side, scene, name) {
        const edgeWidth = this.scene.formData.measuredModel.model.edgeWidth;
        let mat;
        if (this.scene.getActualSide(name).wall === true)
            return;
        let width = side.length - edgeWidth * 2;
        const geom = new THREE.BoxGeometry(width, 230, edgeWidth * 3);
        if (name === this.scene.selectedSide) {
            mat = new THREE.MeshBasicMaterial({
                color: 0x951DFF,
                transparent: true,
                opacity: 0.2,
                visible: true
            });
        }
        else {
            mat = new THREE.MeshBasicMaterial({
                color: 0x28EBFB,
                transparent: true,
                opacity: 0.2,
                visible: false
            });
        }
        const mesh = new THREE.Mesh(geom, mat);
        mesh.name = name;
        mesh.position.copy(side.position);
        mesh.rotateY(degToRad(side.rotationY));
        scene.threeScene.add(mesh);
        scene.pickHandler.pickableObjects = mesh;
    }
    createWallSidePickers(scene) {
        // Don't create the wall picker on the PVC's far side
        this.createWallSidePicker(scene.dimensions.far, scene, "FAR");
        this.createWallSidePicker(scene.dimensions.left, scene, "LEFT");
        this.createWallSidePicker(scene.dimensions.right, scene, "RIGHT");
        this.createWallSidePicker(scene.dimensions.close, scene, "CLOSE");
    }
    createWallSidePicker(side, scene, name) {
        let wallHeight = 450;
        let posY = 225;
        if (scene.formData.measuredModel.model.id < 4) {
            wallHeight = 360;
            posY = 180;
        }
        const geom = new THREE.BoxGeometry(side.length + 60, 1, 450);
        const geomWall = new THREE.BoxGeometry(side.length + 60, wallHeight, 30);
        const mat = new THREE.MeshBasicMaterial({
            color: 0x328ba8,
            transparent: true,
            opacity: 0.2,
            visible: true
        });
        const matWall = new THREE.MeshBasicMaterial({
            color: 0x328ba8,
            transparent: true,
            opacity: 0.8,
            visible: false
        });
        const mesh = new THREE.Mesh(geom, mat);
        const meshWall = new THREE.Mesh(geomWall, matWall);
        mesh.name = name;
        mesh.position.copy(side.position);
        meshWall.position.copy(side.position);
        mesh.position.y = 0;
        meshWall.position.y = posY;
        const edgeWidth = scene.formData.measuredModel.model.edgeWidth;
        // Store the wall's material in an object available from scene. We need this, to get it from the raycastHandler without raycasting it
        this.wallSideMaterialContainer.addMaterial(name, matWall);
        // Set the offset depending of the side's axis
        switch (name) {
            case "LEFT":
                {
                    if (scene.formData.measuredModel.rotated === true) {
                        mesh.position.x -= edgeWidth / 2 + 100;
                        meshWall.position.x -= edgeWidth / 2 + 15;
                    }
                    else {
                        mesh.position.z += edgeWidth / 2 + 100;
                        meshWall.position.z += edgeWidth / 2 + 15;
                    }
                }
                break;
            case "RIGHT":
                {
                    if (scene.formData.measuredModel.rotated === true) {
                        mesh.position.x += edgeWidth / 2 + 100;
                        meshWall.position.x += edgeWidth / 2 + 15;
                    }
                    else {
                        mesh.position.z -= edgeWidth / 2 + 100;
                        meshWall.position.z -= edgeWidth / 2 + 15;
                    }
                }
                break;
            case "CLOSE":
                {
                    if (scene.formData.measuredModel.rotated === true) {
                        mesh.position.z += edgeWidth / 2 + 100;
                        meshWall.position.z += edgeWidth / 2 + 15;
                    }
                    else {
                        mesh.position.x += edgeWidth / 2 + 100;
                        meshWall.position.x += edgeWidth / 2 + 15;
                    }
                }
                break;
            case "FAR":
                {
                    if (scene.formData.measuredModel.rotated === true) {
                        mesh.position.z -= edgeWidth / 2 + 100;
                        meshWall.position.z -= edgeWidth / 2 + 15;
                    }
                    else {
                        mesh.position.x -= edgeWidth / 2 + 100;
                        meshWall.position.x -= edgeWidth / 2 + 15;
                    }
                }
                break;
        }
        mesh.rotateY(degToRad(side.rotationY));
        meshWall.rotateY(degToRad(side.rotationY));
        scene.threeScene.add(mesh, meshWall);
        scene.pickHandler.pickableObjects = mesh;
    }
}
/** Stores the wall picker's materials according to the side. **/
export class WallSideMaterialContainer {
    constructor() {
        this.left = this.right = this.close = this.far = new THREE.MeshBasicMaterial();
    }
    // To add the material to the container
    addMaterial(name, mat) {
        switch (name) {
            case "LEFT":
                {
                    this.left = mat;
                }
                break;
            case "RIGHT":
                {
                    this.right = mat;
                }
                break;
            case "CLOSE":
                {
                    this.close = mat;
                }
                break;
            case "FAR":
                {
                    this.far = mat;
                }
                break;
        }
    }
    /** Sets the material visibility from the {@link PickHandler.raycastHandler} **/
    setMaterialVisibility(name, remove = false) {
        switch (name) {
            case "LEFT":
                {
                    this.left.visible = remove !== true;
                }
                break;
            case "RIGHT":
                {
                    this.right.visible = remove !== true;
                }
                break;
            case "CLOSE":
                {
                    this.close.visible = remove !== true;
                }
                break;
            case "FAR":
                {
                    this.far.visible = remove !== true;
                }
                break;
        }
    }
}
